// CSV-Importer-Logik (rein funktional, ohne React).
// Wird vom AdminImportPage-Wizard verwendet.

import { normalisiereFachrichtungImport } from './fachrichtung.js';

// ---------- Encoding ----------

/**
 * Liest die Datei explizit als ArrayBuffer über die klassische FileReader-API.
 * Wird statt file.arrayBuffer() verwendet, weil sich das Verhalten in einigen
 * Edge-Cases unterscheidet (z. B. wenn Browser-Erweiterungen den Blob-Stream
 * abfangen).
 */
export function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('FileReader error'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * CP850 (IBM 850) — westeuropäische DOS-Codepage. WHATWG/TextDecoder
 * unterstützt CP850 nicht, deshalb der handgeschriebene Lookup für die
 * High-Bytes 0x80–0xFF (jedes Element ist der zugehörige Unicode-Codepoint).
 */
const CP850_HIGH = [
  // 0x80
  0x00c7, 0x00fc, 0x00e9, 0x00e2, 0x00e4, 0x00e0, 0x00e5, 0x00e7,
  0x00ea, 0x00eb, 0x00e8, 0x00ef, 0x00ee, 0x00ec, 0x00c4, 0x00c5,
  // 0x90
  0x00c9, 0x00e6, 0x00c6, 0x00f4, 0x00f6, 0x00f2, 0x00fb, 0x00f9,
  0x00ff, 0x00d6, 0x00dc, 0x00f8, 0x00a3, 0x00d8, 0x00d7, 0x0192,
  // 0xA0
  0x00e1, 0x00ed, 0x00f3, 0x00fa, 0x00f1, 0x00d1, 0x00aa, 0x00ba,
  0x00bf, 0x00ae, 0x00ac, 0x00bd, 0x00bc, 0x00a1, 0x00ab, 0x00bb,
  // 0xB0
  0x2591, 0x2592, 0x2593, 0x2502, 0x2524, 0x00c1, 0x00c2, 0x00c0,
  0x00a9, 0x2563, 0x2551, 0x2557, 0x255d, 0x00a2, 0x00a5, 0x2510,
  // 0xC0
  0x2514, 0x2534, 0x252c, 0x251c, 0x2500, 0x253c, 0x00e3, 0x00c3,
  0x255a, 0x2554, 0x2569, 0x2566, 0x2560, 0x2550, 0x256c, 0x00a4,
  // 0xD0
  0x00f0, 0x00d0, 0x00ca, 0x00cb, 0x00c8, 0x0131, 0x00cd, 0x00ce,
  0x00cf, 0x2518, 0x250c, 0x2588, 0x2584, 0x00a6, 0x00cc, 0x2580,
  // 0xE0
  0x00d3, 0x00df, 0x00d4, 0x00d2, 0x00f5, 0x00d5, 0x00b5, 0x00fe,
  0x00de, 0x00da, 0x00db, 0x00d9, 0x00fd, 0x00dd, 0x00af, 0x00b4,
  // 0xF0
  0x00ad, 0x00b1, 0x2017, 0x00be, 0x00b6, 0x00a7, 0x00f7, 0x00b8,
  0x00b0, 0x00a8, 0x00b7, 0x00b9, 0x00b3, 0x00b2, 0x25a0, 0x00a0,
];

function decodeCp850(buffer) {
  const view = new Uint8Array(buffer);
  let result = '';
  for (let i = 0; i < view.length; i++) {
    const b = view[i];
    if (b < 0x80) {
      result += String.fromCharCode(b);
    } else {
      result += String.fromCharCode(CP850_HIGH[b - 0x80]);
    }
  }
  return result;
}

/** Dispatcher: behandelt CP850 / ibm850 manuell, alles andere via TextDecoder. */
function decodeBuffer(buffer, encoding) {
  if (encoding === 'ibm850' || encoding === 'cp850') {
    return decodeCp850(buffer);
  }
  return new TextDecoder(encoding).decode(buffer);
}

/**
 * Strippt eine UTF-8 BOM (EF BB BF) auf Byte-Ebene, falls vorhanden.
 * Wichtig: muss VOR dem Dekodieren passieren — sonst landen die drei BOM-Bytes
 * bei einer ISO-8859-1-/Windows-1252-Dekodierung als sichtbarer Müll im
 * ersten Header-Feld ("ï»¿Vorname").
 */
function stripBomBytes(buffer) {
  const view = new Uint8Array(buffer);
  if (view.length >= 3 && view[0] === 0xef && view[1] === 0xbb && view[2] === 0xbf) {
    return buffer.slice(3);
  }
  return buffer;
}

const GERMAN_CHARS = new Set(['Ä', 'Ö', 'Ü', 'ä', 'ö', 'ü', 'ß']);

function countGermanChars(text) {
  let count = 0;
  for (const ch of text) {
    if (GERMAN_CHARS.has(ch)) count += 1;
  }
  return count;
}

/**
 * Score-basiertes Encoding-Detect.
 *
 * Algorithmus:
 *   1. Sample auf die ersten 500 Bytes begrenzen
 *   2. UTF-8 strict probieren — wenn ALLE Bytes valide sind, gewinnt UTF-8
 *      sofort (typischer Fall für moderne Quellen)
 *   3. Sonst: ISO-8859-1, MacRoman und CP850 dekodieren und für jedes Encoding
 *      zählen, wie viele deutsche Sonderzeichen (Ä Ö Ü ä ö ü ß) im Text auftauchen
 *   4. Das Encoding mit dem höchsten Score gewinnt
 *      (Tie-Breaker: ISO-8859-1 > MacRoman > CP850 in Listenreihenfolge)
 */
export function detectGermanEncoding(buffer) {
  const sample = buffer.byteLength > 500 ? buffer.slice(0, 500) : buffer;

  // 1) UTF-8 strict
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(sample);
    return 'utf-8';
  } catch {
    // nicht UTF-8 — fall through
  }

  // 2) Single-byte Kandidaten nach German-Char-Score
  const candidates = ['iso-8859-1', 'macintosh', 'ibm850'];
  let best = { encoding: 'iso-8859-1', score: -1 };

  for (const enc of candidates) {
    try {
      const text = decodeBuffer(sample, enc);
      const score = countGermanChars(text);
      if (score > best.score) {
        best = { encoding: enc, score };
      }
    } catch {
      // skip
    }
  }

  return best.encoding;
}

/** Dekodiert eine Datei mit explizit gewähltem Encoding. */
export async function decodeWithEncoding(file, encoding) {
  const raw = await readFileAsArrayBuffer(file);
  const buffer = stripBomBytes(raw);
  return decodeBuffer(buffer, encoding);
}

/**
 * Auto-Detect Wrapper: erkennt Encoding per detectGermanEncoding und dekodiert
 * die ganze Datei.
 */
export async function detectEncodingAndDecode(file) {
  const raw = await readFileAsArrayBuffer(file);
  const buffer = stripBomBytes(raw);
  const encoding = detectGermanEncoding(buffer);
  const text = decodeBuffer(buffer, encoding);
  return { encoding, text };
}

export const ENCODING_OPTIONS = [
  { value: 'auto', label: 'Automatisch erkennen (empfohlen)' },
  { value: 'iso-8859-1', label: 'ISO-8859-1 (Latin-1)' },
  { value: 'windows-1252', label: 'Windows-1252' },
  { value: 'utf-8', label: 'UTF-8' },
  { value: 'macintosh', label: 'MacRoman (Mac-Excel)' },
  { value: 'ibm850', label: 'CP850 / DOS' },
];

// ---------- CSV-Parser ----------

/** Erkennt Semikolon vs Komma anhand der ersten Zeile. */
export function detectDelimiter(text) {
  const firstLine = text.split(/\r?\n/)[0] || '';
  // Innerhalb von Quotes nicht zählen — einfacher Workaround:
  let inQuotes = false;
  let semis = 0;
  let commas = 0;
  for (const c of firstLine) {
    if (c === '"') inQuotes = !inQuotes;
    else if (!inQuotes && c === ';') semis++;
    else if (!inQuotes && c === ',') commas++;
  }
  return semis >= commas ? ';' : ',';
}

/**
 * RFC 4180-konformer Mini-Parser. Unterstützt:
 *  - gequotete Felder mit Trennzeichen, Zeilenumbruch und doppelten Quotes
 *  - \n und \r\n Zeilenenden
 */
function parseRows(text, delimiter) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          // Escaped quote
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }

    if (c === delimiter) {
      row.push(field);
      field = '';
      i++;
      continue;
    }

    if (c === '\r') {
      i++;
      continue;
    }

    if (c === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i++;
      continue;
    }

    field += c;
    i++;
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
}

/**
 * Vollständiges Parsen: liefert Header, Daten als Objekt-Array und das
 * erkannte Trennzeichen.
 */
export function parseCSV(text) {
  const delimiter = detectDelimiter(text);
  const rows = parseRows(text, delimiter);
  if (rows.length === 0) {
    return { delimiter, headers: [], data: [] };
  }

  const headers = rows[0].map((h) => h.trim());
  const data = rows
    .slice(1)
    .filter((row) => row.some((cell) => cell.trim() !== ''))
    .map((row) => {
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = (row[idx] ?? '').trim();
      });
      return obj;
    });

  return { delimiter, headers, data };
}

// ---------- Spalten-Auto-Mapping ----------

export const IMPORT_FIELDS = [
  { key: 'vorname', label: 'Vorname' },
  { key: 'nachname', label: 'Nachname' },
  { key: 'titel', label: 'Anrede / Titel' },
  { key: 'plz', label: 'PLZ' },
  { key: 'ort', label: 'Ort' },
  { key: 'strasse', label: 'Straße' },
  { key: 'telefon', label: 'Telefon' },
  { key: 'email', label: 'E-Mail' },
  { key: 'website', label: 'Website' },
  { key: 'fachgebiet', label: 'Fachgebiet (alt, Freitext)' },
  { key: 'fachgebiet_neu', label: 'Fachrichtung (strukturiert)' },
  { key: 'zusatzinfo', label: 'Zusatzinfo → Praxis-Bemerkung' },
];

const HEURISTICS = {
  vorname: ['vorname', 'first name', 'firstname', 'first', 'given'],
  nachname: ['nachname', 'last name', 'lastname', 'surname', 'familienname'],
  titel: ['titel', 'title', 'anrede', 'salutation'],
  plz: ['plz', 'postleitzahl', 'zip', 'postal'],
  ort: ['ort', 'stadt', 'city', 'town'],
  strasse: ['straße', 'strasse', 'street', 'adresse', 'address'],
  telefon: ['telefon', 'tel.', 'tel ', 'phone', 'fon', 'rufnummer'],
  email: ['e-mail', 'email', 'mail', 'e mail'],
  website: ['website', 'web', 'www', 'url', 'homepage', 'internet'],
  fachgebiet: ['fachgebiet', 'spezialisierung', 'specialty', 'fach'],
  fachgebiet_neu: ['fachrichtung', 'fachgebiet_neu', 'fachrichtung_neu'],
  zusatzinfo: ['bemerkung', 'zusatz', 'notiz', 'kommentar', 'hinweis', 'info', 'comment', 'memo'],
};

/**
 * Schlägt eine Vor-Belegung des Mappings vor, indem für jedes CRM-Feld die
 * erste passende Spalte gesucht wird (case-insensitive substring match).
 * Eine bereits genutzte Spalte wird nicht doppelt vergeben.
 */
export function autoMapColumns(columns) {
  const result = {};
  const used = new Set();
  for (const field of IMPORT_FIELDS) {
    const keywords = HEURISTICS[field.key] || [];
    const match = columns.find(
      (col) => !used.has(col) && keywords.some((kw) => col.toLowerCase().includes(kw))
    );
    result[field.key] = match || '';
    if (match) used.add(match);
  }
  return result;
}

// ---------- Gruppierung ----------

const get = (row, mapping, field) => {
  const col = mapping[field];
  if (!col) return '';
  return (row[col] ?? '').toString().trim();
};

/**
 * Normalisiert PLZ + Straße zu einem stabilen Gruppierungs-Schlüssel.
 *
 * Schritte:
 *   1. PLZ trimmen + alle inneren Whitespaces entfernen
 *      ("10 115" → "10115", " 67067 " → "67067")
 *   2. Straße lowercase, ß → ss, ä/ö/ü → ae/oe/ue,
 *      mehrfache Spaces zu einem reduzieren, trimmen
 *      ("Salzburger Straße 55 " → "salzburger strasse 55")
 *      ("Mühlenweg 3"          → "muehlenweg 3")
 *   3. Beide mit "|" verbinden
 *
 * Die deutsche Transliteration ist wichtig, weil CRM-Listen
 * "Müllerstraße" / "Muellerstrasse" / "Mueller-Straße" frei mischen.
 */
export function buildAddressKey(plz, strasse) {
  const normPlz = (plz || '').replace(/\s+/g, '');
  const normStrasse = (strasse || '')
    .toLowerCase()
    .replace(/[ß\u1E9E]/g, 'ss')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/Ä/g, 'ae')
    .replace(/Ö/g, 'oe')
    .replace(/Ü/g, 'ue')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normPlz && !normStrasse) return '';
  return `${normPlz}|${normStrasse}`;
}

/** Gruppiert die Zeilen nach gewähltem Modus. */
export function groupRows(rows, mapping, mode) {
  const groups = new Map();

  rows.forEach((row, idx) => {
    let key;
    if (mode === 'address') {
      const plz = get(row, mapping, 'plz');
      const strasse = get(row, mapping, 'strasse');
      const addrKey = buildAddressKey(plz, strasse);
      // Wenn weder PLZ noch Straße gemappt sind, fallen wir auf einzelne
      // Zeilen-Keys zurück, damit nicht alles in einen Mega-Bucket läuft.
      key = addrKey ? `addr:${addrKey}` : `row:${idx}`;
    } else if (mode === 'email_domain') {
      const email = get(row, mapping, 'email').toLowerCase();
      const at = email.indexOf('@');
      const domain = at >= 0 ? email.slice(at + 1) : '';
      key = domain ? `dom:${domain}` : `row:${idx}`;
    } else {
      // 'row' = jede Zeile = eigene Praxis
      key = `row:${idx}`;
    }
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });

  return groups;
}

// ---------- Praxisname-Logik ----------

/**
 * Stellt einer Website-URL https:// voran, falls noch kein http(s)://-Schema
 * vorhanden ist. CSV-Exporte enthalten URLs typischerweise als nackte Hostnames
 * ("www.radiologie-vorderpfalz.de"), die so im Browser nicht klickbar wären.
 */
export function normalizeWebsiteUrl(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** Liefert die Domain (ohne Protokoll/www) aus einer URL oder einem nackten Hostname. */
export function extractDomain(value) {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const u = new URL(withScheme);
    return u.hostname.replace(/^www\./i, '');
  } catch {
    return trimmed
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split('/')[0];
  }
}

// Eindeutige Wörter, die nie als Praxisname verwendet werden dürfen
// (alle case-insensitive verglichen).
const NAME_BLACKLIST = new Set([
  'nein',
  'ja',
  'kunde',
  'interessent',
  'aktiv',
  'inaktiv',
  'kein',
  'keine',
  'n/a',
  '-',
  'x',
]);

/** True, wenn der Text nicht als Praxisname verwendet werden darf. */
export function istNameAufBlacklist(text) {
  if (!text) return true;
  const t = text.trim();
  if (!t) return true;
  if (NAME_BLACKLIST.has(t.toLowerCase())) return true;
  if (/^\d+$/.test(t)) return true; // Reine Zahl
  // Einzelnes Wort unter 4 Zeichen ("MVZ" alleine, "ABC" usw.)
  const woerter = t.split(/\s+/).filter(Boolean);
  if (woerter.length === 1 && t.length < 4) return true;
  return false;
}

/**
 * Strenge Prüfung: Darf die Bemerkung als Praxisname genutzt werden?
 * Anforderung: nicht auf Blacklist, mind. 5 Zeichen, mind. 2 Wörter.
 */
export function istBemerkungAlsName(text) {
  if (!text) return false;
  const t = text.trim();
  if (t.length < 5) return false;
  const woerter = t.split(/\s+/).filter(Boolean);
  if (woerter.length < 2) return false;
  if (istNameAufBlacklist(t)) return false;
  return true;
}

/**
 * Baut den Namen aus den Nachnamen aller Ärzte einer Gruppe.
 * - 1: "Dr. Müller"
 * - 2: "Dr. Müller & Dr. Schmidt"
 * - 3: "Dr. Müller, Dr. Schmidt & Dr. Weber"
 * - 4+: "Dr. Müller, Dr. Schmidt u.a."
 * Liefert null, wenn keine verwertbaren Nachnamen vorhanden sind.
 */
export function buildArztname(nachnamen) {
  const unique = [
    ...new Set(
      (nachnamen || [])
        .map((n) => (n || '').trim())
        .filter(Boolean)
    ),
  ];
  if (unique.length === 0) return null;
  if (unique.length === 1) return `Dr. ${unique[0]}`;
  if (unique.length === 2) return `Dr. ${unique[0]} & Dr. ${unique[1]}`;
  if (unique.length === 3)
    return `Dr. ${unique[0]}, Dr. ${unique[1]} & Dr. ${unique[2]}`;
  return `Dr. ${unique[0]}, Dr. ${unique[1]} u.a.`;
}

/** Letzter Fallback-Name, wenn weder Bemerkung, Website noch Arztnamen verwertbar sind. */
export function buildFallbackName(plz, ort) {
  const loc = [plz, ort].map((s) => (s || '').trim()).filter(Boolean).join(' ');
  return loc ? `Unbenannte Praxis ${loc}` : 'Unbenannte Praxis';
}

/** Berechnet alle Namens-Kandidaten für eine Praxis-Gruppe. */
export function buildNameKandidaten({ zusatzinfo, website, kontakte, plz, ort }) {
  const bemerkungKand = istBemerkungAlsName(zusatzinfo) ? zusatzinfo.trim() : null;
  const websiteKand = extractDomain(website) || null;
  const nachnamenKand = buildArztname((kontakte || []).map((k) => k.nachname));
  const fallback = buildFallbackName(plz, ort);
  return { bemerkung: bemerkungKand, website: websiteKand, nachnamen: nachnamenKand, fallback };
}

/**
 * Liefert die initial gewählte Quelle und den passenden Namen für eine Praxis,
 * basierend auf den Kandidaten und der Prioritätsreihenfolge.
 */
export function initialNameAuswahl(kandidaten) {
  if (kandidaten.bemerkung) return { quelle: 'bemerkung', name: kandidaten.bemerkung };
  if (kandidaten.website) return { quelle: 'website', name: kandidaten.website };
  if (kandidaten.nachnamen) return { quelle: 'nachnamen', name: kandidaten.nachnamen };
  // Fallback: Ortsangabe — wird im Manuell-Modus angeboten, damit der User
  // sie sofort editieren kann.
  return { quelle: 'manuell', name: kandidaten.fallback };
}

/** Wandelt eine Gruppe (Zeilen-Array) in eine Praxis-Vorschau um. */
export function buildPraxisVorschau(rows, mapping) {
  const first = rows[0];

  const kontakte = rows.map((row) => ({
    titel: get(row, mapping, 'titel'),
    vorname: get(row, mapping, 'vorname'),
    nachname: get(row, mapping, 'nachname'),
    email: get(row, mapping, 'email'),
    telefon: get(row, mapping, 'telefon'),
  }));

  const zusatzinfo = get(first, mapping, 'zusatzinfo');
  const websiteRaw = get(first, mapping, 'website');
  const plz = get(first, mapping, 'plz');
  const ort = get(first, mapping, 'ort');

  // Kandidaten-Berechnung läuft noch auf der Roh-URL, weil extractDomain
  // ohnehin Schema und www.-Präfix abschneidet.
  const kandidaten = buildNameKandidaten({
    zusatzinfo,
    website: websiteRaw,
    kontakte,
    plz,
    ort,
  });
  const { quelle, name } = initialNameAuswahl(kandidaten);

  return {
    name,
    quelle,
    kandidaten,
    plz,
    ort,
    strasse: get(first, mapping, 'strasse'),
    telefon: get(first, mapping, 'telefon'),
    email: get(first, mapping, 'email'),
    // Beim Import wird die URL normalisiert: nackte Hostnames bekommen
    // automatisch https:// vorangestellt.
    website: normalizeWebsiteUrl(websiteRaw),
    fachgebiet: get(first, mapping, 'fachgebiet'),
    // fachgebiet_neu nur übernehmen, wenn der CSV-Wert exakt einem Slug oder
    // Label entspricht — sonst leer lassen, damit nicht stumm Müll in das
    // strukturierte Feld läuft. Wert kann nachträglich von Hand zugeordnet
    // werden.
    fachgebiet_neu: normalisiereFachrichtungImport(get(first, mapping, 'fachgebiet_neu')),
    // Zusatzinfo wird IMMER als Notiz mitgespeichert, unabhängig davon
    // ob sie als Praxisname genutzt wird.
    bemerkung: zusatzinfo,
    kontakte,
  };
}

/** Macht aus geparsten Daten eine fertige Praxis-Vorschau-Liste. */
export function buildAllVorschauen(rows, mapping, mode) {
  const groups = groupRows(rows, mapping, mode);
  return Array.from(groups.values()).map((g) => buildPraxisVorschau(g, mapping));
}

// ---------- Duplikat-Helper ----------

/**
 * Identisch zur Gruppierungs-Normalisierung — so vergleichen Import-Gruppen
 * und Duplikat-Check exakt dieselben Schlüssel.
 */
export function adressKey(plz, strasse) {
  return buildAddressKey(plz, strasse);
}

export function emailKey(email) {
  return (email ?? '').trim().toLowerCase();
}
